import React, { useState, useEffect } from 'react';
import { supabase_get_client_settings, supabase_update_client_settings, extractErrorMessage } from '../services/supabaseService';
import { Loader2, AlertCircle, CheckCircle2, ListPlus, X, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface CandidateSourcesSettingsTabProps {
    clientId: string;
}

export const CandidateSourcesSettingsTab: React.FC<CandidateSourcesSettingsTabProps> = ({ clientId }) => {
    const [settings, setSettings] = useState<any>(null);
    const [sources, setSources] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [newSource, setNewSource] = useState('');

    useEffect(() => {
        if (clientId) fetchSettings();
    }, [clientId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const data = await supabase_get_client_settings(clientId);
            setSettings(data || {});
            
            // Extract existing sources or provide defaults
            if (data?.candidate_sources && Array.isArray(data.candidate_sources)) {
                setSources(data.candidate_sources);
            } else {
                setSources(['Website', 'LinkedIn', 'Telefoon', 'Anders']);
            }
        } catch (e: any) {
            console.error("Error fetching client settings:", e);
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (updatedSources: string[] = sources) => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const newSettings = { ...settings, candidate_sources: updatedSources };
            await supabase_update_client_settings(clientId, newSettings);
            setSettings(newSettings);
            setSuccess("Instellingen succesvol opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddSource = () => {
        if (!newSource.trim()) return;
        if (sources.includes(newSource.trim())) {
            setError("Deze bron bestaat al.");
            return;
        }
        
        const updated = [...sources, newSource.trim()];
        setSources(updated);
        setNewSource('');
        handleSave(updated);
    };

    const handleRemoveSource = (indexToRemove: number) => {
        const updated = sources.filter((_, idx) => idx !== indexToRemove);
        setSources(updated);
        handleSave(updated);
    };

    const onDragEnd = (result: any) => {
        if (!result.destination) return;
        const items = Array.from(sources);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setSources(items);
        handleSave(items);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-3xl space-y-8 animate-fadeIn">
            <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Kandidaat Bronnen</h2>
                <p className="text-sm text-slate-500 font-medium mt-1">
                    Beheer de lijst met beschikbare categorieën (zoals LinkedIn of Telefoon) waar kandidaten vandaan komen. Deze worden getoond bij het handmatig toevoegen of importeren.
                </p>
                {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 mt-4"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
                {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3 mt-4"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 overflow-hidden">
                <div className="flex gap-2 mb-6">
                    <input 
                        type="text" 
                        value={newSource} 
                        onChange={(e) => setNewSource(e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSource()}
                        placeholder="Nieuwe bron (bijv. Instagram)" 
                        className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-slate-400"
                    />
                    <button 
                        onClick={handleAddSource} 
                        disabled={!newSource.trim() || isSaving}
                        className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <ListPlus size={16} />} Toevoegen
                    </button>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="sources-list">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                                {sources.map((source, index) => (
                                    <Draggable key={source} draggableId={source} index={index}>
                                        {(provided) => (
                                            <div 
                                                ref={provided.innerRef} 
                                                {...provided.draggableProps} 
                                                className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-slate-200 hover:shadow-md transition-all group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div {...provided.dragHandleProps} className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing">
                                                        <GripVertical size={18} />
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-900">{source}</span>
                                                </div>
                                                <button 
                                                    onClick={() => handleRemoveSource(index)}
                                                    className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {sources.length === 0 && (
                    <div className="text-center py-10 text-slate-400 font-medium text-sm">
                        Geen bronnen gevonden. Voeg er een toe om te beginnen.
                    </div>
                )}
            </div>
        </div>
    );
};
